import { Alert, Button, Flex, Modal, Typography } from "antd"
import React, { useEffect, useRef, useState } from "react"
import { Trans, useTranslation } from "react-i18next"

import { CodeInput } from "../../../components/Input/CodeInput.tsx"
import { formatUsPhone } from "../../../components/PhoneInputs/PhoneInput.tsx"
import { smsRefusalText } from "./sms-refusal.ts"
import {
  type SmsRefusal,
  type SmsVerificationState,
  refusalOf,
  sendSmsCode,
  verifySmsCode
} from "./sms-verification.services.ts"

const { Paragraph, Text } = Typography

const CODE_LENGTH = 6
// Carriers check the opt-in against these public pages, so they must stay absolute.
const TERMS_URL = "https://ezebld.ai/terms"
const PRIVACY_URL = "https://ezebld.ai/privacy-policy"
const CHALLENGE_KEY = "sms.verification.challenges"

interface Challenge {
  phone: string
  expiresAt: number
  resendAt: number
}

const readChallenges = (): Record<string, Challenge> => {
  try {
    const stored = JSON.parse(
      window.sessionStorage.getItem(CHALLENGE_KEY) ?? "null"
    ) as Record<string, Challenge> | null
    return stored && typeof stored === "object" ? stored : {}
  } catch {
    return {}
  }
}

const writeChallenges = (challenges: Record<string, Challenge>) => {
  try {
    window.sessionStorage.setItem(CHALLENGE_KEY, JSON.stringify(challenges))
  } catch {
    // Without storage, reopening the dialog only means asking for a new code.
  }
}

const readChallenge = (phone: string): Challenge | null => {
  const challenge = readChallenges()[phone]
  return challenge && challenge.expiresAt > Date.now() ? challenge : null
}

const writeChallenge = (challenge: Challenge) =>
  writeChallenges({ ...readChallenges(), [challenge.phone]: challenge })

const forgetChallenge = (phone: string) => {
  const { [phone]: _forgotten, ...others } = readChallenges()
  writeChallenges(others)
}

const secondsUntil = (moment: number) =>
  Math.max(0, Math.ceil((moment - Date.now()) / 1000))

interface VerifyPhoneModalProps {
  open: boolean
  phone: string
  startNumber?: string | null
  onClose: () => void
  onVerified: (state: SmsVerificationState) => void
}

export function VerifyPhoneModal({
  open,
  phone,
  startNumber,
  onClose,
  onVerified
}: VerifyPhoneModalProps) {
  const { t } = useTranslation()
  const [step, setStep] = useState<"consent" | "code">("consent")
  const [code, setCode] = useState("")
  const [refusal, setRefusal] = useState<SmsRefusal | null>(null)
  const [busy, setBusy] = useState(false)
  const [resendIn, setResendIn] = useState(0)
  const opening = useRef(0)

  useEffect(() => {
    opening.current += 1
    if (!open) return
    const challenge = readChallenge(phone)
    setStep(challenge ? "code" : "consent")
    setCode("")
    setRefusal(null)
    setBusy(false)
    setResendIn(challenge ? secondsUntil(challenge.resendAt) : 0)
  }, [open, phone])

  useEffect(() => {
    if (resendIn <= 0) return
    const timer = setTimeout(() => setResendIn((left) => left - 1), 1000)
    return () => clearTimeout(timer)
  }, [resendIn])

  const send = async () => {
    const sentFrom = opening.current
    const sentTo = phone
    setBusy(true)
    setRefusal(null)
    const response = await sendSmsCode(sentTo)
    if (response.ok && response.data) {
      const { resend_in, expires_in } = response.data.details.data
      writeChallenge({
        phone: sentTo,
        expiresAt: Date.now() + expires_in * 1000,
        resendAt: Date.now() + resend_in * 1000
      })
    }
    if (sentFrom !== opening.current) return
    setBusy(false)
    if (response.ok && response.data) {
      const { resend_in } = response.data.details.data
      setStep("code")
      setCode("")
      setResendIn(resend_in)
      return
    }
    const refused = refusalOf(response)
    if (refused.code === "resend_too_soon" && refused.retry_after) {
      setResendIn(refused.retry_after)
      setStep("code")
    }
    setRefusal(refused)
  }

  const verify = async () => {
    const verifiedFrom = opening.current
    const verifiedFor = phone
    setBusy(true)
    setRefusal(null)
    const response = await verifySmsCode(verifiedFor, code)
    if (response.ok && response.data) forgetChallenge(verifiedFor)
    if (verifiedFrom !== opening.current) return
    setBusy(false)
    if (response.ok && response.data) {
      onVerified(response.data.details.data)
      return
    }
    setRefusal(refusalOf(response))
  }

  const number = formatUsPhone(phone)

  return (
    <Modal
      open={open}
      title={t("smsVerification.dialog.title")}
      onCancel={onClose}
      destroyOnHidden
      footer={
        step === "consent" ? (
          <Flex justify="end" gap={8}>
            <Button key="cancel" onClick={onClose}>
              {t("smsVerification.dialog.cancel")}
            </Button>
            <Button key="send" type="primary" loading={busy} onClick={send}>
              {t("smsVerification.dialog.sendCode")}
            </Button>
          </Flex>
        ) : (
          <Flex justify="space-between" gap={8}>
            <Button key="resend" disabled={busy || resendIn > 0} onClick={send}>
              {resendIn > 0
                ? t("smsVerification.dialog.resendIn", { seconds: resendIn })
                : t("smsVerification.dialog.resend")}
            </Button>
            <Button
              key="verify"
              type="primary"
              loading={busy}
              disabled={code.length !== CODE_LENGTH}
              onClick={verify}
            >
              {t("smsVerification.dialog.verify")}
            </Button>
          </Flex>
        )
      }
    >
      <Flex vertical gap={12}>
        {step === "consent" ? (
          <>
            <Paragraph className="m-0">
              {t("smsVerification.dialog.intro", { number })}
            </Paragraph>
            <Text type="secondary" className="text-xs">
              <Trans
                i18nKey="smsVerification.dialog.disclosure"
                components={{
                  terms: (
                    <a href={TERMS_URL} target="_blank" rel="noreferrer" />
                  ),
                  privacy: (
                    <a href={PRIVACY_URL} target="_blank" rel="noreferrer" />
                  )
                }}
              />
            </Text>
          </>
        ) : (
          <>
            <Paragraph className="m-0">
              {t("smsVerification.dialog.codeSent", { number })}
            </Paragraph>
            <CodeInput
              length={CODE_LENGTH}
              value={code}
              onChange={setCode}
              onInput={(cells) => setCode(cells.join(""))}
              aria-label={t("smsVerification.dialog.codeLabel")}
            />
          </>
        )}
        {refusal && (
          <Alert
            type="error"
            showIcon
            message={smsRefusalText(t, refusal, startNumber)}
          />
        )}
      </Flex>
    </Modal>
  )
}
